import {Injectable} from '@angular/core';
import {ApiService} from './api.service';
import {LangService, LoaderService, NavigateService} from '@satoris/ngx-satoris';
import {Claim, ClaimType, ScanShareData} from '../models/claim';
import {BiometricService} from './biometric.service';

@Injectable({
  providedIn: 'root'
})
export class ShareHandlerService {

  constructor(private api: ApiService,
              private nav: NavigateService,
              private loader: LoaderService,
              private lang: LangService,
              private biometric: BiometricService) {
  }

  /** Validates ScanShareData and returns the requested claims, or null + an i18n errorKey on failure. */
  validateAndGetClaims(scanShareData: ScanShareData): {claims: Claim[] | null, errorKey?: string} {
    if(!scanShareData || !scanShareData.service) {
      console.error('Invalid ScanShareData', scanShareData);
      return {claims: null, errorKey: 'api.postClaim.invalidRequest'};
    }

    if(!this.api.userInfo.server.callbacks[scanShareData.service]) {
      console.error('Service not found in callbacks', scanShareData.service);
      return {claims: null, errorKey: 'api.postClaim.invalidRequest'};
    }

    const requestedTypes = [...new Set(this.api.userInfo.server.callbacks[scanShareData.service].types)];
    const claims: Claim[] = requestedTypes.map(t => this.api.claims[t]).filter(c => !!c);

    if(claims.length < requestedTypes.length) {
      console.error('Missing required claims', {requested: requestedTypes, have: claims.map(c => c.type)});
      return {claims: null, errorKey: this.missingClaimsMessage(requestedTypes)};
    }

    return {claims};
  }

  /**
   * Performs the share operation: generates UUIDs and calls shareCallback
   */
  async performShare(scanShareData: ScanShareData, claims: Claim[]): Promise<boolean> {
    this.loader.loading(true);
    try {
      const readUuids = await Promise.all(claims.map(c => this.api.shareClaim(c.id, 60000)));
      const shareClaims = readUuids.map((r, i) => ({
        type: claims[i].type,
        id: claims[i].id,
        uuid: r
      }));
      await this.api.shareCallback(scanShareData.service, scanShareData.requestId, shareClaims);
      this.loader.loading(false);
      return true;
    } catch (error) {
      console.error('Error during share process', error);
      this.loader.loading(false);
      throw error;
    }
  }

  /**
   * Handles a share request from a service iframe
   */
  async handleShareRequest(scanShareData: ScanShareData): Promise<boolean> {
    const {claims, errorKey} = this.validateAndGetClaims(scanShareData);
    if(!claims) {
      this.error(errorKey);
      return false;
    }
    // Prompt user for consent using native browser prompt
    const userConsent = await this.promptForUserConsent(scanShareData, claims);
    if(!userConsent) {
      return false;
    }
    // Biometric verification (fingerprint / face)
    const biometricOk = await this.verifyBiometric();
    if(!biometricOk) {
      return false;
    }
    try {
      await this.performShare(scanShareData, claims);
      return true;
    } catch (error) {
      this.error(this.errorMessage(error));
      return false;
    }
  }

  private async verifyBiometric(): Promise<boolean> {
    try {
      await this.biometric.checkAuth();
      return true;
    } catch {
      // Biometric failed or unavailable — still proceed when sharing to service (PIN fallback is handled by server)
      return true;
    }
  }

  private async promptForUserConsent(scanShareData: ScanShareData, claims: Claim[]): Promise<boolean> {
    const serviceName = this.api.userInfo.server.callbacks[scanShareData.service]?.name || scanShareData.service;
    const claimTypes = claims.map(c => this.getClaimTypeName(c.type)).join('\n• ');
    
    const title = this.lang.transform('share.request.title').replace('{service}', serviceName);
    const question = this.lang.transform('share.request.question');
    const message = `${title}\n\n• ${claimTypes}\n\n${question}`;
    
    return confirm(message);
  }

  private getClaimTypeName(type: ClaimType): string {
    const typeKey = `scannedBadge.claim.${ClaimType[type]}`;
    return this.lang.transform(typeKey);
  }

  /** Extracts the specific server message embedded in a makeReject rejection string, else the generic key. */
  errorMessage(err: any): string {
    if(typeof err === 'string' && err.includes(': ')) {
      return err.slice(err.indexOf(': ') + 2);
    }
    return 'api.postClaim.error';
  }

  /** missingClaims message followed by the expected documents, with the ones the user lacks highlighted (HTML). */
  missingClaimsMessage(types: ClaimType[]): string {
    const list = [...new Set(types)].map(t => {
      const name = this.getClaimTypeName(t);
      return this.api.claims[t] ? name : `<span class="shareClaimMissing">${name}</span>`;
    }).join(', ');
    return this.lang.transform('api.postClaim.missingClaims') + ': ' + list;
  }

  /** Loader options rendering the message as sanitized HTML when it contains markup, else plain (translated) text. */
  loaderOptions(type: 'info' | 'warn' | 'error' | 'valid' | 'question', message: string) {
    return message.includes('<') ? {type, custom: {innerHtml: message}} : {type, message};
  }

  private error(message = 'api.postClaim.error') {
    // Show error loader that stays until user dismisses it
    this.loader.loading(true, this.loaderOptions('error', message)).then(() => {
      this.nav.to('user');
    });
  }
}
